import React from 'react';
import {
  AppState,
  EmitterSubscription,
  Keyboard,
  KeyboardEvent,
  TextInput,
} from 'react-native';
import { captureException } from '@/utils/sentry';

/*
 * Diagnostic boundary around the root ToastContextProvider's KeyboardAvoidingView.
 * Includes richer context when "Maximum update depth exceeded" in
 * KeyboardAvoidingView fires. Also renders the fallback (toasts without
 * keyboard-avoiding) so the app stays usable and toasts keep rendering until
 * the next reload.
 */

const KB_EVENT_BUFFER_SIZE = 30;
interface KbEventSnapshot {
  endCoordinates?: KeyboardEvent['endCoordinates'];
  time: number;
  type: string;
}

interface State {
  hasError: boolean;
}

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export class ToastKavErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };
  private kbEvents: KbEventSnapshot[] = [];
  private subs: EmitterSubscription[] = [];

  componentDidMount() {
    const push = (type: string) => (e: KeyboardEvent) => {
      this.kbEvents.push({
        endCoordinates: e?.endCoordinates,
        time: Date.now(),
        type,
      });
      if (this.kbEvents.length > KB_EVENT_BUFFER_SIZE) {
        this.kbEvents.shift();
      }
    };
    this.subs = [
      Keyboard.addListener('keyboardWillShow', push('willShow')),
      Keyboard.addListener('keyboardDidShow', push('didShow')),
      Keyboard.addListener('keyboardWillHide', push('willHide')),
      Keyboard.addListener('keyboardDidHide', push('didHide')),
      Keyboard.addListener('keyboardWillChangeFrame', push('willChangeFrame')),
    ];
  }

  componentWillUnmount() {
    this.subs.forEach((s) => s.remove());
    this.subs = [];
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    let hasFocusedInput: boolean | null = null;
    try {
      hasFocusedInput = TextInput.State.currentlyFocusedInput() !== null;
    } catch {
      // ignore
    }

    captureException(error, {
      extra: {
        appState: AppState.currentState,
        componentStack: info.componentStack,
        hasFocusedInput,
        keyboardVisible: Keyboard.isVisible?.() ?? null,
        recentKeyboardEvents: this.kbEvents.slice(),
      },
      tags: {
        boundary: 'toast-kav',
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
